import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, RadioGroup, RadioGroupItem } from '@parrot-software/design-system';
import { ArrowLeft, ChevronRight, Loader2, PencilLine, Plus } from '@parrot-software/design-system/icons';
import { toast } from 'sonner';
import { cn } from '@parrot-software/utils';

import type {
  CsfDataViewDto,
  InvoiceDataViewDto,
  PaymentMethodViewDto,
  StripeSessionDto,
} from '@subscriptions/application/dtos';
import { PaymentMethodTypes, SubscriptionStatuses } from '@subscriptions/domain/types';
import {
  EmptyState,
  ErrorState,
  WizardStepBackButton,
  WizardStepFooter,
} from '@subscriptions/presentation/components/base';
import { CsfUploadSection, InvoiceDataForm } from '@subscriptions/presentation/components/specific/InvoiceDataForm';
import {
  InvoiceDataCard,
  InvoiceDataListSkeleton,
  NoInvoicingConfirmationDialog,
} from '@subscriptions/presentation/components/specific/InvoiceDataList';
import { resolveSessionMode } from '@subscriptions/presentation/components/specific/BlockCreationWizard/sessionMode';
import {
  isReactivationOrigin,
  resolveWizardOrigin,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import {
  AnalyticsStatuses,
  InvoicingChoices,
  InvoicingMethods,
  InvoicingSources,
  NO_INVOICING_VALUE,
  TrackedPaymentMethods,
  WizardSteps,
} from '@subscriptions/presentation/constants';
import {
  useFiscalRegimes,
  useGenerateBlockPaymentSession,
  useGenerateBlockReference,
  useInvoiceData,
  useListSavedCards,
  useSubscriptionAnalytics,
  useSubscriptionBlock,
  useUpdateBlockInvoiceData,
} from '@subscriptions/presentation/hooks';
import { mapCsfDataToFormValues, resolveSubscriptionErrorMessage } from '@subscriptions/presentation/utils';

import type { PaymentMethodValue } from './BlockCreationStepPayment';
import { SPEI_PAYMENT_METHOD_VALUE } from './BlockCreationStepPayment';

type BlockCreationStepInvoiceDataProps = {
  blockUuid: string;
  selectedMethod: PaymentMethodValue;
  onCardSessionReady: (session: StripeSessionDto) => void;
  onSavedCardsAvailable: (cards: PaymentMethodViewDto[]) => void;
  onSpeiCompleted: (paymentMethod: PaymentMethodViewDto) => void;
};

type WizardMode = 'list' | 'create';

/**
 * Step 3 of the block creation wizard — fiscal data (invoice data) association.
 *
 * When the user re-enters the wizard for an already-configured block (one that has a
 * payment method), the previous fiscal choice arrives pre-selected: the associated invoice
 * data, or "No quiero facturar" when none was linked. During first-time creation nothing is
 * pre-selected so the user must choose. An explicit user selection always takes precedence.
 *
 * This step is the wizard's commit point: the payment method chosen in the previous step
 * arrives via `selectedMethod`, and Continuar triggers the corresponding flow —
 * `generateReference` for SPEI (→ `onSpeiCompleted`) or the card branch. With an active
 * license (`AVAILABLE`) the card branch first queries the org's saved cards: when at
 * least one ACTIVE card exists it advances to the saved-cards step (→
 * `onSavedCardsAvailable`) without creating a Stripe session; when there are none — or
 * the listing fails (silent degradation, the use case logs it) — it falls back to
 * `generateSession(mode, isUpdate)` (→ `onCardSessionReady`). `mode` comes from
 * {@link resolveSessionMode}; `isUpdate` is true when the block already has an attached
 * payment method. Errors keep the user on this step and surface a toast. The CLABE / Stripe
 * secrets returned by the API are never rendered or logged here.
 *
 * @param props - Component properties
 * @returns React element rendering the step
 */
export const BlockCreationStepInvoiceData = ({
  blockUuid,
  selectedMethod,
  onCardSessionReady,
  onSavedCardsAvailable,
  onSpeiCompleted,
}: BlockCreationStepInvoiceDataProps) => {
  const { t, i18n } = useTranslation('subscriptions');
  const { search } = useLocation();
  const { data: invoiceData, isLoading, isError, refetch } = useInvoiceData();
  const { data: block } = useSubscriptionBlock(blockUuid);
  const { data: fiscalRegimes } = useFiscalRegimes();
  const { mutateAsync: updateBlockInvoiceData, isLoading: isUpdating } = useUpdateBlockInvoiceData();
  const { trackInvoicingSelected, trackPaymentMethodSaved, trackPaymentStepViewed } = useSubscriptionAnalytics();
  const origin = resolveWizardOrigin(search);
  const { mutateAsync: generateReference, isLoading: isGeneratingReference } = useGenerateBlockReference();
  const { mutateAsync: generateSession, isLoading: isGeneratingSession } = useGenerateBlockPaymentSession();
  const { mutateAsync: listSavedCards, isLoading: isListingSavedCards } = useListSavedCards();
  const [mode, setMode] = useState<WizardMode>('list');
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);
  const [isNoInvoicingDialogOpen, setIsNoInvoicingDialogOpen] = useState(false);
  const [csfData, setCsfData] = useState<CsfDataViewDto | undefined>(undefined);
  const [showManualForm, setShowManualForm] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Map CSF data to form prefill values when extraction succeeds
  const prefillValues = useMemo(() => (csfData ? mapCsfDataToFormValues(csfData, fiscalRegimes) : undefined), [
    csfData,
    fiscalRegimes,
  ]);

  /** Stores the extracted CSF data to derive prefill values for the form. */
  const handleCsfExtracted = useCallback((extracted: CsfDataViewDto) => {
    setCsfData(extracted);
  }, []);

  /** Updates the uploaded file state (controlled mode for CsfUploadSection). */
  const handleFileChange = useCallback((file: File | null) => {
    setUploadedFile(file);
    // When file is removed, reset to initial state (show dropzone only)
    if (!file) {
      setCsfData(undefined);
      setShowManualForm(false);
    }
  }, []);

  // On re-entry from the block detail the block is already configured (it has a
  // payment method): a missing `invoiceData` then means the user previously chose
  // "No quiero facturar", so pre-select that option. During first-time creation the
  // block has no payment method yet, so nothing is pre-selected and the user must
  // make an explicit choice. An explicit selection always takes precedence.
  const isConfiguredBlock = Boolean(block?.paymentMethod);
  const blockInvoiceSelection = block?.invoiceData?.uuid ?? (isConfiguredBlock ? NO_INVOICING_VALUE : undefined);
  const selectedInvoiceValue = selectedValue ?? blockInvoiceSelection;

  const associate = useCallback(
    async (invoiceDataUuid: string | null) => {
      const previous = selectedValue;
      const nextValue = invoiceDataUuid ?? NO_INVOICING_VALUE;
      setSelectedValue(nextValue);

      const choice = invoiceDataUuid ? InvoicingChoices.INVOICE : InvoicingChoices.NO_INVOICE;
      const method = invoiceDataUuid ? InvoicingMethods.EXISTING : undefined;
      const common = { blockUuid, choice, method, source: InvoicingSources.WIZARD };

      try {
        await updateBlockInvoiceData({ blockUuid, dto: { invoiceDataUuid } });
        trackInvoicingSelected({ ...common, status: AnalyticsStatuses.SUCCESS });
      } catch {
        trackInvoicingSelected({ ...common, status: AnalyticsStatuses.FAILED });
        setSelectedValue(previous);
        toast.error(t('blockCreationWizard.errors.invoiceAssociateFailed'));
      }
    },
    [blockUuid, selectedValue, t, trackInvoicingSelected, updateBlockInvoiceData],
  );

  const handleValueChange = useCallback(
    (value: string) => {
      if (value === selectedInvoiceValue || isUpdating) return;

      if (value === NO_INVOICING_VALUE) {
        setIsNoInvoicingDialogOpen(true);
        return;
      }

      void associate(value);
    },
    [associate, isUpdating, selectedInvoiceValue],
  );

  const handleConfirmNoInvoicing = useCallback(async () => {
    setIsNoInvoicingDialogOpen(false);
    await associate(null);
  }, [associate]);

  const handleCancelNoInvoicing = useCallback(() => {
    setIsNoInvoicingDialogOpen(false);
  }, []);

  const handleNoInvoicingDialogChange = useCallback((open: boolean) => {
    if (!open) setIsNoInvoicingDialogOpen(false);
  }, []);

  const handleCreateSuccess = useCallback(
    async (created: InvoiceDataViewDto) => {
      const common = {
        blockUuid,
        choice: InvoicingChoices.INVOICE,
        method: InvoicingMethods.NEW,
        source: InvoicingSources.WIZARD,
      };

      try {
        await updateBlockInvoiceData({ blockUuid, dto: { invoiceDataUuid: created.uuid } });
        trackInvoicingSelected({ ...common, status: AnalyticsStatuses.SUCCESS });
        setSelectedValue(created.uuid);
        setMode('list');
      } catch {
        trackInvoicingSelected({ ...common, status: AnalyticsStatuses.FAILED });
        toast.error(t('blockCreationWizard.errors.invoiceAssociateFailed'));
      }
    },
    [blockUuid, t, trackInvoicingSelected, updateBlockInvoiceData],
  );

  const isGenerating = isGeneratingReference || isGeneratingSession || isListingSavedCards;

  // Once per mount, like the method step: without the guard, changing the method in place would
  // emit again and inflate the denominator of this step.
  const hasTrackedInvoiceStep = useRef(false);
  useEffect(() => {
    if (hasTrackedInvoiceStep.current) return;
    hasTrackedInvoiceStep.current = true;
    trackPaymentStepViewed({
      blockUuid,
      origin,
      paymentMethod:
        selectedMethod === SPEI_PAYMENT_METHOD_VALUE ? TrackedPaymentMethods.SPEI : TrackedPaymentMethods.CARD,
      step: WizardSteps.INVOICE,
    });
  }, [blockUuid, origin, selectedMethod, trackPaymentStepViewed]);

  const handleComplete = useCallback(async () => {
    if (selectedMethod === SPEI_PAYMENT_METHOD_VALUE) {
      try {
        const paymentMethod = await generateReference(blockUuid);
        trackPaymentMethodSaved({
          blockUuid,
          origin,
          paymentMethod: TrackedPaymentMethods.SPEI,
          status: AnalyticsStatuses.SUCCESS,
        });
        onSpeiCompleted(paymentMethod);
      } catch {
        trackPaymentMethodSaved({
          blockUuid,
          origin,
          paymentMethod: TrackedPaymentMethods.SPEI,
          status: AnalyticsStatuses.FAILED,
        });
        toast.error(t('blockCreationWizard.errors.paymentReferenceFailed'));
      }
      return;
    }

    const isLicenseActive = block?.status === SubscriptionStatuses.AVAILABLE;
    const isUpdate = Boolean(block?.paymentMethod?.uuid);

    // Active license only: offer the org's saved cards before opening the Stripe
    // checkout. A failed listing degrades silently to the checkout (the use case
    // already logged it) — this query must never block the wizard.
    //
    // A reactivation skips the offer entirely. Attaching a saved card only swaps the payment
    // method, it never raises the reactivation charge, and the license status cannot rule the
    // branch out on its own: a churned block can still read as AVAILABLE.
    if (isLicenseActive && !isReactivationOrigin(search)) {
      try {
        const savedCards = await listSavedCards(PaymentMethodTypes.CARD);
        if (savedCards.length > 0) {
          onSavedCardsAvailable(savedCards);
          return;
        }
      } catch {
        // Silent fallback to the current checkout flow.
      }
    }

    const sessionMode = resolveSessionMode(isLicenseActive, search);

    try {
      const session = await generateSession({ blockUuid, isUpdate, mode: sessionMode });
      onCardSessionReady(session);
    } catch (error) {
      toast.error(resolveSubscriptionErrorMessage(error, t, i18n, 'blockCreationWizard.errors.paymentSessionFailed'));
    }
  }, [
    block,
    blockUuid,
    generateReference,
    generateSession,
    i18n,
    listSavedCards,
    onCardSessionReady,
    onSavedCardsAvailable,
    onSpeiCompleted,
    origin,
    search,
    selectedMethod,
    t,
    trackPaymentMethodSaved,
  ]);

  const handleBackToList = useCallback(() => {
    setMode('list');
    setCsfData(undefined);
    setShowManualForm(false);
    setIsExtracting(false);
    setUploadedFile(null);
  }, []);

  const handleOpenCreate = useCallback(() => setMode('create'), []);

  /** Skips CSF upload and shows the empty form for manual entry. */
  const handleSelectManual = useCallback(() => {
    setShowManualForm(true);
  }, []);

  /** Tracks extraction loading state to hide the manual-fill button while processing. */
  const handleLoadingChange = useCallback((loading: boolean) => {
    setIsExtracting(loading);
  }, []);

  // In create mode, show form when user uploaded a doc OR clicked manual
  const showForm = Boolean(csfData) || showManualForm;
  // Show manual button only when form is not yet visible and not extracting
  const showManualButton = !showForm && !isExtracting;

  return (
    <div
      className={cn(
        'flex h-[100dvh] flex-col items-center overflow-hidden',
        'px-4 pt-20 pb-[max(1.5rem,env(safe-area-inset-bottom))]',
        'md:px-6 md:pb-12 md:pt-20',
      )}
    >
      <div className="flex min-h-0 w-full max-w-[1020px] flex-1 flex-col gap-6">
        <h1 className="shrink-0 text-2xl font-semibold leading-9 text-foreground">
          {t('blockCreationWizard.invoice.title')}
        </h1>

        {mode === 'list' && (
          <>
            <NoInvoicingConfirmationDialog
              isSubmitting={isUpdating}
              onCancel={handleCancelNoInvoicing}
              onConfirm={handleConfirmNoInvoicing}
              onOpenChange={handleNoInvoicingDialogChange}
              open={isNoInvoicingDialogOpen}
            />

            <div
              className={cn(
                'flex min-h-[300px] flex-col gap-6 overflow-y-auto',
                'rounded-xl border bg-background p-4 shadow-xs md:p-6',
              )}
            >
              <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-col gap-0.5">
                  <h2 className="text-xl font-semibold leading-8">{t('blockCreationWizard.invoice.list.cardTitle')}</h2>
                  <p className="text-sm leading-5 text-muted-foreground">
                    {t('blockCreationWizard.invoice.list.cardDescription')}
                  </p>
                </div>
                <Button className="md:w-auto" onClick={handleOpenCreate} type="button" variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('blockCreationWizard.invoice.list.addInvoiceData')}
                </Button>
              </div>

              {isLoading && <InvoiceDataListSkeleton />}

              {isError && !invoiceData && (
                <ErrorState
                  onRetry={() => refetch()}
                  retryLabel={t('blockCreationWizard.invoice.list.retry')}
                  title={t('blockCreationWizard.invoice.list.loadingError')}
                />
              )}

              {!isLoading && invoiceData && (
                <RadioGroup className="gap-4" onValueChange={handleValueChange} value={selectedInvoiceValue ?? ''}>
                  <div className="flex items-center gap-2 py-1">
                    <RadioGroupItem className="h-4 w-4" id={NO_INVOICING_VALUE} value={NO_INVOICING_VALUE} />
                    <label className="text-sm" htmlFor={NO_INVOICING_VALUE}>
                      {t('blockCreationWizard.invoice.list.noInvoicing')}
                    </label>
                  </div>

                  {invoiceData.length === 0 ? (
                    <EmptyState
                      description={t('blockCreationWizard.invoice.list.emptyDescription')}
                      title={t('blockCreationWizard.invoice.list.emptyTitle')}
                    />
                  ) : (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {invoiceData.map((item) => (
                        <InvoiceDataCard
                          invoiceData={item}
                          isSelectable
                          isSelected={selectedInvoiceValue === item.uuid}
                          key={item.uuid}
                          showActions={false}
                        />
                      ))}
                    </div>
                  )}
                </RadioGroup>
              )}
            </div>

            <WizardStepFooter className="shrink-0">
              <Button
                className="w-full md:w-auto"
                disabled={!selectedInvoiceValue || isUpdating || isGenerating}
                onClick={handleComplete}
                type="button"
              >
                {t('blockCreationWizard.invoice.list.submit')}
                {isGenerating ? (
                  <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                ) : (
                  <ChevronRight className="ml-2 h-4 w-4" />
                )}
              </Button>
            </WizardStepFooter>
          </>
        )}

        {mode === 'create' && (
          <div
            className={cn(
              'flex min-h-0 flex-col gap-6 overflow-y-auto',
              'rounded-xl border bg-background p-4 shadow-xs md:p-6',
            )}
          >
            <div className="flex flex-col items-stretch gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex flex-col gap-0.5">
                <h2 className="text-xl font-semibold leading-8">{t('blockCreationWizard.invoice.create.cardTitle')}</h2>
                <p className="text-sm leading-5 text-muted-foreground">
                  {t('blockCreationWizard.invoice.create.cardDescription')}
                </p>
              </div>
              <Button className="md:w-auto" onClick={handleBackToList} type="button" variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('blockCreationWizard.invoice.create.back')}
              </Button>
            </div>

            {!showForm && (
              <>
                <CsfUploadSection
                  file={uploadedFile}
                  onExtracted={handleCsfExtracted}
                  onFileChange={handleFileChange}
                  onLoadingChange={handleLoadingChange}
                />

                {showManualButton && (
                  <Button
                    className="h-auto w-fit p-0 text-xs font-normal text-muted-foreground hover:text-primary"
                    onClick={handleSelectManual}
                    type="button"
                    variant="link"
                  >
                    <PencilLine className="mr-1 h-3 w-3" />
                    {t('blockCreationWizard.invoice.create.manualFill')}
                  </Button>
                )}
              </>
            )}

            {showForm && (
              <InvoiceDataForm
                fieldsClassName="grid flex-none grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
                footerClassName={cn(
                  'mt-6 flex flex-row items-center justify-end gap-2',
                  '[&>button[type=submit]]:w-full md:[&>button[type=submit]]:w-auto',
                  '[&>button[type=button]]:hidden',
                )}
                footerStart={<WizardStepBackButton className="mr-auto" />}
                headerContent={
                  <div className="col-span-full mb-4">
                    <CsfUploadSection
                      file={uploadedFile}
                      onExtracted={handleCsfExtracted}
                      onFileChange={handleFileChange}
                      onLoadingChange={handleLoadingChange}
                    />
                  </div>
                }
                mode="create"
                onCancel={handleBackToList}
                onSuccess={handleCreateSuccess}
                prefillValues={prefillValues}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
